import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import actions from "actions";
import * as selectors from "selectors";
import AssignClinicianConfirmDialog from "../Dialogs/AssignClinicianConfirmDialog";
import AssignFamilyNavigatorConfirmDialog from "../Dialogs/AssignFamilyNavigatorConfirmDialog";
import RemoveClinicianConfirmDialog from "../Dialogs/RemoveClinicianConfirmDialog";
import AssignedClinicians from "../Widgets/AssignedClinicians";
import AssignFamilyNavigator, {
  FamilyNavigatorCandidate,
} from "../Widgets/AssignedClinicians/AssignFamilyNavigator";
import { useGetFamilyNavigators } from "hooks/api/user/useGetFamilyNavigators";
import { useUpdateFamilyNavigator } from "hooks/api/client/useUpdateFamilyNavigator";
import { UserPermissions } from "types/permissions";

/**
 * Lightweight wrapper that provides clinician assignment dialog state and handlers for AssignedClinicians widget.
 * Used as a section within multi-section pages like CareTeam.
 *
 * Manages:
 * - Dialog open/close state for assign and remove operations
 * - Dispatch of clinician assignment actions
 * - Confirmation dialogs for clinician changes
 */
const CareTeamPageShell: React.FC = () => {
  const dispatch = useDispatch();
  const customerDetails: any = useSelector(selectors.getCustomerDetails);
  const workflowStatus: any = useSelector(selectors.getCustomerWorkflowStatus);

  const [assignClinicianOpen, setAssignClinicianOpen] = useState(false);
  const [assignedRole, setAssignedRole] = useState<string | null>(null);
  const [selectedClinician, setSelectedClinician] = useState<any>(null);
  const [assignClinicianConfirmOpen, setAssignClinicianConfirmOpen] = useState(false);
  const [confirmSent, setConfirmSent] = useState(false);
  const [removeClinicianConfirmOpen, setRemoveClinicianConfirmOpen] = useState(false);
  const [clinicianToRemove, setClinicianToRemove] = useState<any>(null);
  const [removeConfirmSent, setRemoveConfirmSent] = useState(false);
  const [familyNavigatorOpen, setFamilyNavigatorOpen] = useState(false);
  const [selectedFamilyNavigator, setSelectedFamilyNavigator] =
    useState<FamilyNavigatorCandidate | null>(null);
  const [familyNavigatorConfirmOpen, setFamilyNavigatorConfirmOpen] = useState(false);
  const [familyNavigatorConfirmSent, setFamilyNavigatorConfirmSent] = useState(false);

  const isActive = customerDetails?.details?.is_active;
  const clientId = customerDetails?.details?.client_id;
  const editDisabled = !isActive || workflowStatus === "SELF_SERVICE_INTAKE";
  const familyNavigatorEditDisabled = !isActive;
  const userPermissions = useSelector(selectors.getUserPermissionsList) as UserPermissions | null;
  const { familyNavigators, familyNavigatorsLoading } = useGetFamilyNavigators({
    enabled: familyNavigatorOpen && Boolean(userPermissions?.edit_client_info),
  });
  const { updateFamilyNavigator, isUpdatingFamilyNavigator } = useUpdateFamilyNavigator({
    onSuccess: (_response: unknown, variables: { clientId: string }) => {
      dispatch(actions.loadCustomerDetails(variables.clientId));
      setFamilyNavigatorConfirmOpen(false);
      setFamilyNavigatorOpen(false);
      setSelectedFamilyNavigator(null);
      setFamilyNavigatorConfirmSent(false);
    },
    onError: () => setFamilyNavigatorConfirmSent(false),
  });

  const submitAssignClinician = () => {
    if (!selectedClinician || !clientId) return;

    if (!customerDetails?.clinician && assignedRole === "primary") {
      dispatch(actions.assignClinician(clientId, selectedClinician.user_id));
    } else if (customerDetails?.clinician && assignedRole === "primary") {
      dispatch(actions.switchClinician(selectedClinician.clinician_id));
    } else {
      dispatch(actions.assignSecondaryClinician(clientId, selectedClinician.user_id));
    }

    setConfirmSent(true);
    setAssignClinicianConfirmOpen(false);
  };

  const submitRemoveClinician = () => {
    if (!clientId) return;
    dispatch(actions.removeSecondaryClinician(clientId));
    setRemoveConfirmSent(true);
    setRemoveClinicianConfirmOpen(false);
  };

  return (
    <>
      <AssignedClinicians
        editDisabled={editDisabled}
        familyNavigatorEditDisabled={familyNavigatorEditDisabled}
        onCloseAssignClinician={() => setAssignClinicianOpen(false)}
        onOpenAssignClinician={(role) => {
          setAssignedRole(role);
          setAssignClinicianOpen(true);
        }}
        onOpenAssignClinicianConfirm={(clinicianObj) => {
          setSelectedClinician(clinicianObj);
          setConfirmSent(false);
          setAssignClinicianOpen(false);
          setAssignClinicianConfirmOpen(true);
        }}
        onOpenRemoveClinicianConfirm={(clinicianObj) => {
          setClinicianToRemove(clinicianObj);
          setRemoveConfirmSent(false);
          setRemoveClinicianConfirmOpen(true);
        }}
        onOpenAssignFamilyNavigator={() => {
          setFamilyNavigatorConfirmSent(false);
          setFamilyNavigatorOpen(true);
        }}
        assignedRole={assignedRole ?? undefined}
        assignClinicianOpen={assignClinicianOpen}
      />

      <AssignClinicianConfirmDialog
        open={assignClinicianConfirmOpen}
        closeDialog={() => {
          setAssignClinicianConfirmOpen(false);
          setConfirmSent(false);
        }}
        assignedRole={assignedRole}
        selectedClinician={selectedClinician ?? {}}
        submitAssignClinician={submitAssignClinician}
        confirmSent={confirmSent}
      />

      <RemoveClinicianConfirmDialog
        open={removeClinicianConfirmOpen}
        closeDialog={() => {
          setRemoveClinicianConfirmOpen(false);
          setRemoveConfirmSent(false);
        }}
        selectedClinician={clinicianToRemove ?? { image_url: "", first_name: "", last_name: "" }}
        removeSecondaryClinician={submitRemoveClinician}
        confirmSent={removeConfirmSent}
      />

      <AssignFamilyNavigator
        open={familyNavigatorOpen}
        onClose={() => setFamilyNavigatorOpen(false)}
        onSelectFamilyNavigator={(candidate) => {
          setSelectedFamilyNavigator(candidate);
          setFamilyNavigatorOpen(false);
          setFamilyNavigatorConfirmSent(false);
          setFamilyNavigatorConfirmOpen(true);
        }}
        familyNavigators={familyNavigators}
        familyNavigatorsLoading={familyNavigatorsLoading}
        currentFamilyNavigatorId={
          customerDetails?.details?.family_navigator_user_id?.toString() || null
        }
      />

      <AssignFamilyNavigatorConfirmDialog
        open={familyNavigatorConfirmOpen}
        closeDialog={() => {
          setFamilyNavigatorConfirmOpen(false);
          setSelectedFamilyNavigator(null);
          setFamilyNavigatorConfirmSent(false);
        }}
        selectedFamilyNavigator={selectedFamilyNavigator || { id: "", name: "" }}
        submitAssignment={() => {
          if (!selectedFamilyNavigator || !clientId) return;

          setFamilyNavigatorConfirmSent(true);
          updateFamilyNavigator({
            clientId: String(clientId),
            familyNavigatorUserId: selectedFamilyNavigator.id,
          });
        }}
        confirmSent={familyNavigatorConfirmSent || isUpdatingFamilyNavigator}
      />
    </>
  );
};

export default CareTeamPageShell;
